import React, { useState, useRef, useEffect } from 'react';
import {
    Send, Minimize2, Building2, Users, ShoppingCart, HeartHandshake, TrendingUp,
    UserCircle, Wrench, Factory, LineChart, Cog, Scale
} from 'lucide-react';

// Enhanced departments with unique colors and icons
const departments = [
    {
        id: 1,
        name: 'Sales',
        greeting: "Hi! I'm your DIO Sales assistant. How can I help grow your business today? 💼",
        isInternal: false,
        color: "blue",
        icon: ShoppingCart,
        gradientFrom: "from-blue-600",
        gradientTo: "to-cyan-400"
    },
    {
        id: 2,
        name: 'Customer Service',
        greeting: "Welcome to DIO Customer Service! How can I assist you today? 🤝",
        isInternal: false,
        color: "emerald",
        icon: HeartHandshake,
        gradientFrom: "from-emerald-500",
        gradientTo: "to-green-400"
    },
    {
        id: 3,
        name: 'Marketing',
        greeting: "Hello! I'm your DIO Marketing assistant. Let's boost your brand presence! 📈",
        isInternal: false,
        color: "rose",
        icon: TrendingUp,
        gradientFrom: "from-rose-500",
        gradientTo: "to-pink-400"
    },
    {
        id: 4,
        name: 'HR',
        greeting: "Welcome to DIO HR! How can I help with your HR needs today? 👥",
        isInternal: true,
        color: "violet",
        icon: UserCircle,
        gradientFrom: "from-violet-600",
        gradientTo: "to-purple-400"
    },
    {
        id: 5,
        name: 'Technical Support',
        greeting: "Hi! DIO Tech Support here. What technical challenges can I help with? 🔧",
        isInternal: false,
        color: "amber",
        icon: Wrench,
        gradientFrom: "from-amber-500",
        gradientTo: "to-yellow-400"
    },
    {
        id: 6,
        name: 'Manufacturing',
        greeting: "Welcome to DIO Manufacturing! How can I assist with production today? 🏭",
        isInternal: true,
        color: "orange",
        icon: Factory,
        gradientFrom: "from-orange-500",
        gradientTo: "to-amber-400"
    },
    {
        id: 7,
        name: 'Finance',
        greeting: "Hello! I'm your DIO Finance assistant. How can I help with your financial data? 💹",
        isInternal: true,
        color: "teal",
        icon: LineChart,
        gradientFrom: "from-teal-500",
        gradientTo: "to-emerald-400"
    },
    {
        id: 8,
        name: 'Operations',
        greeting: "Welcome to DIO Operations! How can I help optimize your processes? ⚙️",
        isInternal: true,
        color: "sky",
        icon: Cog,
        gradientFrom: "from-sky-500",
        gradientTo: "to-blue-400"
    },
    {
        id: 9,
        name: 'Legal',
        greeting: "Hi! I'm your DIO Legal assistant. How can I help you today? ⚖️",
        isInternal: true,
        color: "indigo",
        icon: Scale,
        gradientFrom: "from-indigo-600",
        gradientTo: "to-violet-400"
    }
];

const ChatMessage = ({ message, isUser, department }) => {
    // Define all the color variations explicitly
    const getColors = () => {
        switch (department.color) {
            case "blue":
                return {
                    avatarBg: "bg-blue-600",
                    avatarShadow: "shadow-blue-500/30",
                    userBg: "bg-blue-500/20",
                    userBorder: "border-blue-500/40",
                    botBorder: "border-blue-500/40",
                    msgShadow: "shadow-blue-500/10"
                };
            case "emerald":
                return {
                    avatarBg: "bg-emerald-600",
                    avatarShadow: "shadow-emerald-500/30",
                    userBg: "bg-emerald-500/20",
                    userBorder: "border-emerald-500/40",
                    botBorder: "border-emerald-500/40",
                    msgShadow: "shadow-emerald-500/10"
                };
            case "rose":
                return {
                    avatarBg: "bg-rose-600",
                    avatarShadow: "shadow-rose-500/30",
                    userBg: "bg-rose-500/20",
                    userBorder: "border-rose-500/40",
                    botBorder: "border-rose-500/40",
                    msgShadow: "shadow-rose-500/10"
                };
            case "violet":
                return {
                    avatarBg: "bg-violet-600",
                    avatarShadow: "shadow-violet-500/30",
                    userBg: "bg-violet-500/20",
                    userBorder: "border-violet-500/40",
                    botBorder: "border-violet-500/40",
                    msgShadow: "shadow-violet-500/10"
                };
            case "amber":
                return {
                    avatarBg: "bg-amber-600",
                    avatarShadow: "shadow-amber-500/30",
                    userBg: "bg-amber-500/20",
                    userBorder: "border-amber-500/40",
                    botBorder: "border-amber-500/40",
                    msgShadow: "shadow-amber-500/10"
                };
            case "orange":
                return {
                    avatarBg: "bg-orange-600",
                    avatarShadow: "shadow-orange-500/30",
                    userBg: "bg-orange-500/20",
                    userBorder: "border-orange-500/40",
                    botBorder: "border-orange-500/40",
                    msgShadow: "shadow-orange-500/10"
                };
            case "teal":
                return {
                    avatarBg: "bg-teal-600",
                    avatarShadow: "shadow-teal-500/30",
                    userBg: "bg-teal-500/20",
                    userBorder: "border-teal-500/40",
                    botBorder: "border-teal-500/40",
                    msgShadow: "shadow-teal-500/10"
                };
            case "sky":
                return {
                    avatarBg: "bg-sky-600",
                    avatarShadow: "shadow-sky-500/30",
                    userBg: "bg-sky-500/20",
                    userBorder: "border-sky-500/40",
                    botBorder: "border-sky-500/40",
                    msgShadow: "shadow-sky-500/10"
                };
            case "indigo":
                return {
                    avatarBg: "bg-indigo-600",
                    avatarShadow: "shadow-indigo-500/30",
                    userBg: "bg-indigo-500/20",
                    userBorder: "border-indigo-500/40",
                    botBorder: "border-indigo-500/40",
                    msgShadow: "shadow-indigo-500/10"
                };
            default:
                return {
                    avatarBg: "bg-blue-600",
                    avatarShadow: "shadow-blue-500/30",
                    userBg: "bg-blue-500/20",
                    userBorder: "border-blue-500/40",
                    botBorder: "border-blue-500/40",
                    msgShadow: "shadow-blue-500/10"
                };
        }
    };

    const colors = getColors();

    return (
        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
            {!isUser && (
                <div className={`w-8 h-8 rounded-full mr-2 overflow-hidden flex-shrink-0 ${colors.avatarBg} shadow-lg ${colors.avatarShadow}`}>
                    <img
                        src="/Images/dio-bot.jpg"
                        alt="DIO Bot"
                        className="w-full h-full object-cover"
                    />
                </div>
            )}
            <div className={`max-w-[80%] p-4 rounded-xl break-words ${isUser
                ? `${colors.userBg} text-white rounded-br-none border ${colors.userBorder}`
                : `bg-slate-800/70 text-white rounded-bl-none border ${colors.botBorder}`
                } shadow-lg ${colors.msgShadow}`}>
                <p className="text-sm whitespace-pre-wrap">{message}</p>
            </div>
        </div>
    );
};

const DepartmentChatbot = ({ department, onNavigate, isExpanded, onMinimize }) => {
    const [messages, setMessages] = useState([
        { text: department.greeting, isUser: false }
    ]);
    const [inputText, setInputText] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const messagesEndRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            const chatContainer = messagesEndRef.current.parentElement;
            chatContainer.scrollTop = chatContainer.scrollHeight;
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const getBotResponse = async (userInput) => {
    const currentConversation = messages.map(msg => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.text
    }));

    // Create a context string, e.g., "sales_chat", "customer_service_chat"
    const contextKey = `${department.name.toLowerCase().replace(/\s+/g, '_')}_chat`;

    const response = await fetch('/api/chat', { // <-- FIXED: Use relative path
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            messages: [...currentConversation, { role: 'user', content: userInput }],
            chatContext: contextKey //  <-- ADD THIS
            // The specific system message ("You are the Sales assistant...")
            // will now be constructed on the backend based on this contextKey.
        })
    });

        if (!response.ok) throw new Error('Failed to get response');
        const data = await response.json();
        return data.message;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!inputText.trim() || isProcessing) return;

        const userMessage = { text: inputText, isUser: true };
        setMessages(prev => [...prev, userMessage]);
        setInputText('');
        setIsProcessing(true);

        try {
            const response = await getBotResponse(inputText);
            setMessages(prev => [...prev, { text: response, isUser: false }]);
        } catch (error) {
            setMessages(prev => [...prev, {
                text: "Connection error. Please try again or contact us directly.",
                isUser: false
            }]);
        } finally {
            setIsProcessing(false);
        }
    };

    const Icon = department.icon;

    // Define all the color variations for each element with glow effects removed
    const getColors = () => {
        switch (department.color) {
            case "blue":
                return {
                    headerBorder: "border-blue-500/20",
                    iconColor: "text-blue-400",
                    iconHoverColor: "text-blue-300",
                    buttonText: "text-blue-400",
                    buttonHoverText: "text-blue-300",
                    buttonBorder: "border-blue-500/30",
                    inputBorder: "border-blue-500/30",
                    inputFocus: "focus:ring-blue-400",
                    submitBg: "bg-blue-500/30",
                    submitHoverBg: "hover:bg-blue-500/40",
                    underline: "bg-blue-500"
                };
            case "emerald":
                return {
                    headerBorder: "border-emerald-500/20",
                    iconColor: "text-emerald-400",
                    iconHoverColor: "text-emerald-300",
                    buttonText: "text-emerald-400",
                    buttonHoverText: "text-emerald-300",
                    buttonBorder: "border-emerald-500/30",
                    inputBorder: "border-emerald-500/30",
                    inputFocus: "focus:ring-emerald-400",
                    submitBg: "bg-emerald-500/30",
                    submitHoverBg: "hover:bg-emerald-500/40",
                    underline: "bg-emerald-500"
                };
            case "rose":
                return {
                    headerBorder: "border-rose-500/20",
                    iconColor: "text-rose-400",
                    iconHoverColor: "text-rose-300",
                    buttonText: "text-rose-400",
                    buttonHoverText: "text-rose-300",
                    buttonBorder: "border-rose-500/30",
                    inputBorder: "border-rose-500/30",
                    inputFocus: "focus:ring-rose-400",
                    submitBg: "bg-rose-500/30",
                    submitHoverBg: "hover:bg-rose-500/40",
                    underline: "bg-rose-500"
                };
            case "violet":
                return {
                    headerBorder: "border-violet-500/20",
                    iconColor: "text-violet-400",
                    iconHoverColor: "text-violet-300",
                    buttonText: "text-violet-400",
                    buttonHoverText: "text-violet-300",
                    buttonBorder: "border-violet-500/30",
                    inputBorder: "border-violet-500/30",
                    inputFocus: "focus:ring-violet-400",
                    submitBg: "bg-violet-500/30",
                    submitHoverBg: "hover:bg-violet-500/40",
                    underline: "bg-violet-500"
                };
            case "amber":
                return {
                    headerBorder: "border-amber-500/20",
                    iconColor: "text-amber-400",
                    iconHoverColor: "text-amber-300",
                    buttonText: "text-amber-400",
                    buttonHoverText: "text-amber-300",
                    buttonBorder: "border-amber-500/30",
                    inputBorder: "border-amber-500/30",
                    inputFocus: "focus:ring-amber-400",
                    submitBg: "bg-amber-500/30",
                    submitHoverBg: "hover:bg-amber-500/40",
                    underline: "bg-amber-500"
                };
            case "orange":
                return {
                    headerBorder: "border-orange-500/20",
                    iconColor: "text-orange-400",
                    iconHoverColor: "text-orange-300",
                    buttonText: "text-orange-400",
                    buttonHoverText: "text-orange-300",
                    buttonBorder: "border-orange-500/30",
                    inputBorder: "border-orange-500/30",
                    inputFocus: "focus:ring-orange-400",
                    submitBg: "bg-orange-500/30",
                    submitHoverBg: "hover:bg-orange-500/40",
                    underline: "bg-orange-500"
                };
            case "teal":
                return {
                    headerBorder: "border-teal-500/20",
                    iconColor: "text-teal-400",
                    iconHoverColor: "text-teal-300",
                    buttonText: "text-teal-400",
                    buttonHoverText: "text-teal-300",
                    buttonBorder: "border-teal-500/30",
                    inputBorder: "border-teal-500/30",
                    inputFocus: "focus:ring-teal-400",
                    submitBg: "bg-teal-500/30",
                    submitHoverBg: "hover:bg-teal-500/40",
                    underline: "bg-teal-500"
                };
            case "sky":
                return {
                    headerBorder: "border-sky-500/20",
                    iconColor: "text-sky-400",
                    iconHoverColor: "text-sky-300",
                    buttonText: "text-sky-400",
                    buttonHoverText: "text-sky-300",
                    buttonBorder: "border-sky-500/30",
                    inputBorder: "border-sky-500/30",
                    inputFocus: "focus:ring-sky-400",
                    submitBg: "bg-sky-500/30",
                    submitHoverBg: "hover:bg-sky-500/40",
                    underline: "bg-sky-500"
                };
            case "indigo":
                return {
                    headerBorder: "border-indigo-500/20",
                    iconColor: "text-indigo-400",
                    iconHoverColor: "text-indigo-300",
                    buttonText: "text-indigo-400",
                    buttonHoverText: "text-indigo-300",
                    buttonBorder: "border-indigo-500/30",
                    inputBorder: "border-indigo-500/30",
                    inputFocus: "focus:ring-indigo-400",
                    submitBg: "bg-indigo-500/30",
                    submitHoverBg: "hover:bg-indigo-500/40",
                    underline: "bg-indigo-500"
                };
            default:
                return {
                    headerBorder: "border-blue-500/20",
                    iconColor: "text-blue-400",
                    iconHoverColor: "text-blue-300",
                    buttonText: "text-blue-400",
                    buttonHoverText: "text-blue-300",
                    buttonBorder: "border-blue-500/30",
                    inputBorder: "border-blue-500/30",
                    inputFocus: "focus:ring-blue-400",
                    submitBg: "bg-blue-500/30",
                    submitHoverBg: "hover:bg-blue-500/40",
                    underline: "bg-blue-500"
                };
        }
    };

    const colors = getColors();

    return (
        <div
            className="relative h-full transform transition-all duration-300 hover:scale-[1.01]"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Clean background without glow */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-white/[0.15] to-white/[0.05] backdrop-blur-xl border border-white/10" />

            {/* Simple hover effect */}
            <div className="absolute inset-0 rounded-xl bg-white/5 opacity-0 hover:opacity-100 transition-all duration-300" />

            {/* Main Content */}
            <div className="relative flex flex-col h-full rounded-2xl backdrop-blur-sm overflow-hidden">
                {/* Header */}
                <div className={`flex items-center justify-between p-4 text-white border-b ${colors.headerBorder} bg-slate-800/90`}>
                    <div className="flex items-center gap-4">
                        <div className="bg-slate-800 p-3 rounded-xl border border-slate-700 shadow-sm">
                            <Icon className={`w-5 h-5 ${colors.iconColor}`} />
                        </div>
                        <h3 className="font-bold text-lg">{department.name}</h3>
                    </div>
                    {isExpanded ? (
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                onMinimize();
                            }}
                            className={`px-4 py-2 flex items-center gap-2 ${colors.buttonText} transition-colors duration-300`}
                        >
                            <Minimize2 className="w-4 h-4" />
                            Minimize
                        </button>
                    ) : (
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                onNavigate(department.id);
                            }}
                            className={`px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg ${colors.buttonText} transition-all duration-300 border border-slate-700`}
                        >
                            Expand
                        </button>
                    )}
                </div>

                {/* Chat Area */}
                <div className="flex-1 flex flex-col min-h-0 bg-slate-900/80">
                    <div className="flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                        {messages.map((message, index) => (
                            <ChatMessage
                                key={index}
                                message={message.text}
                                isUser={message.isUser}
                                department={department}
                            />
                        ))}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area with clean design */}
                    <div className="p-4 border-t border-slate-700 bg-slate-800/80">
                        <form onSubmit={handleSubmit} className="flex gap-2">
                            <input
                                type="text"
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                placeholder={`Message ${department.name}...`}
                                className="flex-1 px-4 py-3 rounded-xl bg-slate-700/50 text-white text-sm placeholder-slate-400 focus:outline-none focus:ring-2 border border-slate-600 focus:ring-slate-500 transition-all duration-300"
                                disabled={isProcessing}
                            />
                            <button
                                type="submit"
                                className="text-white p-3 rounded-xl transition-all duration-300 disabled:opacity-50 bg-slate-700 hover:bg-slate-600 disabled:hover:bg-slate-700 border border-slate-600"
                                disabled={!inputText.trim() || isProcessing}
                            >
                                <Send className="w-5 h-5" />
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ChatbotGrid = ({ onNavigate }) => {
    const [expandedId, setExpandedId] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    // Updated colors for a more elegant look
    const bgGradient = "bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900";

    // Add global styles for beautiful background
    useEffect(() => {
        document.body.classList.add('bg-gradient-to-br', 'from-gray-900', 'via-slate-900', 'to-gray-900');
        // Add stars background
        const starsContainer = document.createElement('div');
        starsContainer.classList.add('stars-container');
        starsContainer.style.position = 'fixed';
        starsContainer.style.top = '0';
        starsContainer.style.left = '0';
        starsContainer.style.width = '100%';
        starsContainer.style.height = '100%';
        starsContainer.style.overflow = 'hidden';
        starsContainer.style.zIndex = '-1';
        document.body.appendChild(starsContainer);

        // Create stars
        for (let i = 0; i < 100; i++) {
            const star = document.createElement('div');
            star.classList.add('star');
            star.style.position = 'absolute';
            star.style.width = `${Math.random() * 2 + 1}px`;
            star.style.height = star.style.width;
            star.style.backgroundColor = 'white';
            star.style.borderRadius = '50%';
            star.style.opacity = Math.random() * 0.8 + 0.2;
            star.style.top = `${Math.random() * 100}%`;
            star.style.left = `${Math.random() * 100}%`;
            star.style.animation = `twinkle ${Math.random() * 5 + 5}s infinite`;
            starsContainer.appendChild(star);
        }

        // Add keyframes for twinkling
        const style = document.createElement('style');
        style.textContent = `
            @keyframes twinkle {
                0% { opacity: 0.2; }
                50% { opacity: 1; }
                100% { opacity: 0.2; }
            }
        `;
        document.head.appendChild(style);

        // Cleanup
        return () => {
            document.body.classList.remove('bg-gradient-to-br', 'from-gray-900', 'via-slate-900', 'to-gray-900');
            document.body.removeChild(starsContainer);
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        // Animate in the chatbots
        setIsLoaded(true);
    }, []);

    const handleExpand = (id) => {
        setExpandedId(id);
    };

    const handleMinimize = () => {
        setExpandedId(null);
    };

    // Prevent page scroll when chatbot is expanded
    useEffect(() => {
        if (expandedId) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [expandedId]);

    return (
        <div className="p-6 min-h-screen">
            <div className="max-w-7xl mx-auto relative">
                {/* Grid layout with staggered animation */}
                <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-opacity duration-500 
                    ${expandedId ? 'opacity-0 pointer-events-none absolute inset-0' : 'opacity-100'}`}>
                    {departments.map((dept, index) => (
                        <div
                            key={dept.id}
                            className="h-[32rem] opacity-0 animate-slideUp"
                            style={{
                                animationDelay: `${index * 0.1 + 0.3}s`,
                                animationFillMode: 'forwards',
                                animationDuration: '0.7s'
                            }}
                        >
                            <DepartmentChatbot
                                department={dept}
                                onNavigate={handleExpand}
                                isExpanded={false}
                                onMinimize={handleMinimize}
                            />
                        </div>
                    ))}
                </div>
                {/* Expanded chatbot with smooth animation */}
                {expandedId && (
                    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-40 flex items-center justify-center p-6 animate-fadeIn">
                        <div className="w-full max-w-4xl h-[40rem] animate-scaleUp">
                            <DepartmentChatbot
                                department={departments.find(d => d.id === expandedId)}
                                onNavigate={handleExpand}
                                isExpanded={true}
                                onMinimize={handleMinimize}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatbotGrid;